import React from "react";

import { CheckIcon } from "@heroicons/react/outline";

import Image from "next/image";

import CalendarIcon from "public/images/icons/solar-financing-operating-system/Calendar-blue.svg";
import ShieldIcon from "public/images/icons/solar-financing-operating-system/SOC2-type2-certified.svg";

import DiscoverEnerfloButtonWithOverlay from "components/discover-enerflo-button-with-overlay";
import Heading from "components/heading";
import HeroSection from "components/hero-section";

const features = [
  "Supports Solar Loan, Lease, PPA, and Pre-Paid PPA products",
  "Dev hours and integration infrastructure covered by Enerflo",
  "SOC 2 Type II certified",
  "Go live in weeks — nationwide installer network ready from day one",
];

const FeatureBlock = ({ description }: { description: string }) => (
  <li className="flex items-start gap-3 text-left">
    <div className="inline-block p-1 rounded-full shadow-md bg-primary text-white">
      <CheckIcon className="h-4 w-4" />
    </div>
    <p className="text-navy text-lg">{description}</p>
  </li>
);

const title = "Get Started";

const GetStarted = () => (
  <div className="bg-gray-100">
    <HeroSection title={title} className="flex-col gap-y-6 lg:flex-row lg:space-x-0">
      <HeroSection.Content className="w-full sm:px-6 lg:w-1/2 lg:px-10 lg:text-left">
        <header className="mb-12">
          <HeroSection.Tagline className="text-primary mb-4">{title}</HeroSection.Tagline>
          <Heading tag="h2" className="mb-6">
            <span>Ready to launch your</span>
            <br />
            <span className="text-primary inline-block my-1">Residential solar financing program?</span>
          </Heading>
          <p className="text-gray-600">
            Book a discovery call with our team. We’ll take the time to understand your solar loan, TPO, or PPA program,
            your goals, and what your current infrastructure can and can’t do. From there, we’ll craft a solution built
            around your specific needs — and have your solar financing program up and running in weeks, not months.
          </p>
        </header>

        <ul className="space-y-6">
          {features.map((feature, index) => (
            <FeatureBlock key={index} description={feature} />
          ))}
        </ul>
      </HeroSection.Content>

      <aside className="p-3 lg:p-8 flex-grow w-full sm:w-4/5 lg:w-1/2">
        <div className="shadow-lg bg-white rounded-xl py-6 px-4 xs:p-8 text-center">
          <div className="space-y-6 md:space-y-8 max-w-lg mx-auto">
            <figure className="h-10 w-10 p-1 border border-primary bg-primary/20 rounded-lg mx-auto">
              <Image src={CalendarIcon} alt="Book a Discovery Call - Icon" />
            </figure>
            <div>
              <Heading tag="h3" size="h4" className="mb-2">
                Book a Discovery Call
              </Heading>
              <p>Book a discovery call with an Enerflo Financial Operating System Specialist to learn more.</p>
            </div>

            <div>
              <DiscoverEnerfloButtonWithOverlay
                buttonClassName="w-full text-sm sm:text-base"
                meetingSrc="https://meetings.hubspot.com/laurel-heller/native-financing-os-discovery"
              />
              <p className="text-xs text-gray-700 mt-2">No risk. No spam. No commitments.</p>
            </div>

            <hr className="space-y-4 border-gray-300" />

            <div className="text-gray-500 text-xs mt-2 flex justify-center items-center gap-2">
              <figure className="h-5 w-5 inline-block">
                <Image src={ShieldIcon} alt="SOC 2 Type II Certified - Icon" />
              </figure>
              SOC 2 Type II Certified
            </div>
          </div>
        </div>
      </aside>
    </HeroSection>
  </div>
);

export default GetStarted;
